/**
 * V2ConflictDetector
 *
 * Detects logical conflicts between conditions in a route.
 * Handles V2 operators including containsAll, containsAny, exists, notExists.
 *
 * @module v2-condition-editor
 */

import type { V2ConditionRow, V2ConditionConflict, V2ConflictType } from '../types/v2-condition-editor.types';
import { V2_CONFLICT_MESSAGES } from '../constants/v2-condition-constants';

export class V2ConflictDetector {
  /**
   * Detect conflicts between conditions.
   *
   * Detection rules:
   * 1. **mutual_exclusion**: Same variable with eq + neq for same value → impossible
   * 2. **always_false**: Same variable with eq for two different values (AND logic) → impossible
   * 3. **redundant**: Same variable with both exists and eq → exists is redundant
   * 4. **mutual_exclusion**: Same variable with exists + notExists → impossible
   * 5. **always_false**: Number range contradictions (gt X AND lt Y where Y <= X)
   *
   * @param conditions - Array of condition rows to check
   * @returns Array of detected conflicts
   */
  detectConflicts(conditions: V2ConditionRow[]): V2ConditionConflict[] {
    const conflicts: V2ConditionConflict[] = [];
    const validConditions = conditions.filter((c) => c.key && c.operator);

    for (let i = 0; i < validConditions.length; i++) {
      for (let j = i + 1; j < validConditions.length; j++) {
        const a = validConditions[i];
        const b = validConditions[j];

        // Only compare conditions on the same variable
        if (a.key !== b.key) continue;

        const conflict = this.checkPair(a, b);
        if (conflict) {
          conflicts.push(conflict);
        }
      }
    }

    return conflicts;
  }

  /**
   * Check a pair of conditions for conflicts.
   */
  private checkPair(
    a: V2ConditionRow,
    b: V2ConditionRow,
  ): V2ConditionConflict | null {
    // exists + notExists → mutual exclusion
    if (
      (a.operator === 'exists' && b.operator === 'notExists') ||
      (a.operator === 'notExists' && b.operator === 'exists')
    ) {
      return this.conflict('mutual_exclusion', [a.id, b.id]);
    }

    // eq + neq same value → mutual exclusion
    if (a.operator === 'eq' && b.operator === 'neq' && a.value === b.value) {
      return this.conflict('mutual_exclusion', [a.id, b.id]);
    }
    if (a.operator === 'neq' && b.operator === 'eq' && a.value === b.value) {
      return this.conflict('mutual_exclusion', [a.id, b.id]);
    }

    // eq + eq different values (AND logic) → always false
    if (a.operator === 'eq' && b.operator === 'eq' && a.value !== b.value) {
      return this.conflict('always_false', [a.id, b.id]);
    }

    // exists + eq/neq/in/notIn → exists is redundant (eq implies exists)
    if (a.operator === 'exists' && this.impliesExists(b.operator)) {
      return this.conflict('redundant', [a.id, b.id]);
    }
    if (b.operator === 'exists' && this.impliesExists(a.operator)) {
      return this.conflict('redundant', [b.id, a.id]);
    }

    // Number range contradictions
    const rangeConflict = this.checkRangeConflict(a, b);
    if (rangeConflict) return rangeConflict;

    return null;
  }

  /**
   * Check for numeric range contradictions.
   * gt X AND lt Y where Y <= X → always false
   */
  private checkRangeConflict(
    a: V2ConditionRow,
    b: V2ConditionRow,
  ): V2ConditionConflict | null {
    const aNum = parseFloat(a.value);
    const bNum = parseFloat(b.value);

    if (isNaN(aNum) || isNaN(bNum)) return null;

    // gt/gte X AND lt/lte Y
    if (this.isLowerBound(a.operator) && this.isUpperBound(b.operator)) {
      if (this.rangeIsEmpty(a.operator, aNum, b.operator, bNum)) {
        return this.conflict('always_false', [a.id, b.id]);
      }
    }

    if (this.isLowerBound(b.operator) && this.isUpperBound(a.operator)) {
      if (this.rangeIsEmpty(b.operator, bNum, a.operator, aNum)) {
        return this.conflict('always_false', [a.id, b.id]);
      }
    }

    return null;
  }

  /**
   * Check if a range defined by lower and upper bounds is empty.
   */
  private rangeIsEmpty(
    lowerOp: string,
    lowerVal: number,
    upperOp: string,
    upperVal: number,
  ): boolean {
    // gt X AND lt Y → empty when Y <= X
    if (lowerOp === 'gt' && upperOp === 'lt') return upperVal <= lowerVal;
    // gt X AND lte Y → empty when Y <= X
    if (lowerOp === 'gt' && upperOp === 'lte') return upperVal <= lowerVal;
    // gte X AND lt Y → empty when Y <= X
    if (lowerOp === 'gte' && upperOp === 'lt') return upperVal <= lowerVal;
    // gte X AND lte Y → empty when Y < X
    if (lowerOp === 'gte' && upperOp === 'lte') return upperVal < lowerVal;
    return false;
  }

  private isLowerBound(op: string): boolean {
    return op === 'gt' || op === 'gte';
  }

  private isUpperBound(op: string): boolean {
    return op === 'lt' || op === 'lte';
  }

  /**
   * Operators that imply the variable exists.
   */
  private impliesExists(op: string): boolean {
    return op !== 'exists' && op !== 'notExists';
  }

  private conflict(type: V2ConflictType, rowIds: string[]): V2ConditionConflict {
    return {
      type,
      rowIds,
      message: V2_CONFLICT_MESSAGES[type.toUpperCase() as keyof typeof V2_CONFLICT_MESSAGES],
    };
  }
}

/** Singleton instance */
export const v2ConflictDetector = new V2ConflictDetector();
